/*------------------------------------------------------------------------------
   About      : Publish / unpublish / discard / QA-status actions
                (epic DA-697, II-FE-5)

                Shared by the list screen's bulk bar and the edit screen's
                action row so both report outcomes identically.

                Two behaviours worth reading before changing anything here:

                1. **Publishing resets `verificationStatus` to TODO.** Settled,
                   not an open question: `actions[READY_TO_PUBLISH]` is
                   `[PUBLISH]` only, so an insight left at READY_TO_PUBLISH
                   could not be edited again without walking the status back by
                   hand on every edit. So this REFETCHES after publish rather
                   than patching state optimistically — the row's status AND its
                   `allowedTransitions` both change.

                2. **Batches collect per-item failures.** One bad row does not
                   fail the batch, so every action returns a three-way outcome
                   (see `insightBatchResult.ts`) instead of a boolean.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useCallback, useState } from 'react'

import { useGlobalConfig }       from 'context/GlobalContext'
import { ANALYTICS_EVENTS }      from 'framework/analytics/AnalyticsEvents'
import { useAppDispatch }        from 'store/Store'
import { CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'

import { InsightApis }           from '../api/insightAPI'
import { BatchOutcome,
         readBatchOutcome }      from '../utils/insightBatchResult'
import { setInsightsFromPartitions,
         upsertInsight }         from '../store/insightUISlice'

export interface UseInsightWorkflowActions {
  isRunning : boolean
  outcome  ?: BatchOutcome
  clearOutcome : () => void
  publish        : (documentIds: string[]) => Promise<BatchOutcome | undefined>
  unpublish      : (documentIds: string[]) => Promise<BatchOutcome | undefined>
  discardDrafts  : (documentIds: string[]) => Promise<BatchOutcome | undefined>
  changeQaStatus : (documentId: string, next: CONTENT_VERIFICATION_STATUS) => Promise<BatchOutcome | undefined>
}

export function useInsightWorkflowActions(
  utilityDocumentId : string | undefined,
  pilotId           : string | undefined,
): UseInsightWorkflowActions {

  const { rc }   = useGlobalConfig()
  const dispatch = useAppDispatch()

  const [isRunning, setIsRunning] = useState(false)
  const [outcome, setOutcome]     = useState<BatchOutcome | undefined>()

  /**
   * Refetches the whole pilot.
   *
   * Deliberately a full refetch rather than a targeted patch: publish resets
   * verificationStatus server-side and recomputes `allowedTransitions` for the
   * affected records, and unpublish/discard move records BETWEEN the draft /
   * modified / published partitions — which is what determines the STATUS
   * column. Reconstructing all of that client-side would be guessing.
   *
   * `getInsights` takes the CMS `utilityDocumentId`, not the business `pilotId`
   * below — quill's internal API filters on the utility relation's documentId.
   */
  const refetch = useCallback(async () => {
    if (!utilityDocumentId) return
    const resp = await InsightApis.getInsights(rc, utilityDocumentId)
    if (resp.data) dispatch(setInsightsFromPartitions(resp.data))
  }, [rc, utilityDocumentId, dispatch])

  const runBatch = useCallback(async (
    documentIds : string[],
    verb        : string,
    call        : (ids: string[]) => Promise<any>,
    events      : { ok : string; fail ?: string },
  ) => {
    if (!pilotId || !documentIds.length) return undefined

    setIsRunning(true)
    setOutcome(undefined)

    const result = await call(documentIds)
    const next   = readBatchOutcome(result, documentIds.length, verb)

    // Refetch on anything other than an outright transport failure: a PARTIAL
    // means some rows really did change, and leaving the table stale would show
    // the user pre-action state for records that already moved.
    if (next.kind !== 'FAILURE' || next.succeeded > 0) await refetch()

    setIsRunning(false)
    setOutcome(next)

    rc.analytics.trackEvent(
      next.kind === 'FAILURE' && events.fail ? events.fail : events.ok,
      { attempted : next.attempted, failed : next.failed },
    )

    return next
  }, [pilotId, refetch, rc])

  const publish = useCallback((documentIds: string[]) => runBatch(
    documentIds, 'published',
    (ids) => InsightApis.publishAll(rc, { pilotId : pilotId!, data : ids }),
    { ok : ANALYTICS_EVENTS.NBI_INSIGHTS.PUBLISHED, fail : ANALYTICS_EVENTS.NBI_INSIGHTS.PUBLISH_FAILED },
  ), [runBatch, rc, pilotId])

  const unpublish = useCallback((documentIds: string[]) => runBatch(
    documentIds, 'unpublished',
    (ids) => InsightApis.unpublishAll(rc, { pilotId : pilotId!, data : ids }),
    { ok : ANALYTICS_EVENTS.NBI_INSIGHTS.UNPUBLISHED },
  ), [runBatch, rc, pilotId])

  const discardDrafts = useCallback((documentIds: string[]) => runBatch(
    documentIds, 'discarded',
    (ids) => InsightApis.discardAllDrafts(rc, { pilotId : pilotId!, data : ids }),
    { ok : ANALYTICS_EVENTS.NBI_INSIGHTS.DRAFTS_DISCARDED },
  ), [runBatch, rc, pilotId])

  /**
   * A QA-status move is an ordinary field update, so it goes through
   * `updateAll` like any other edit — there is no dedicated transition route.
   */
  const changeQaStatus = useCallback(async (
    documentId : string,
    next       : CONTENT_VERIFICATION_STATUS,
  ) => {
    if (!pilotId) return undefined

    setIsRunning(true)
    setOutcome(undefined)

    const result = await InsightApis.updateAll(rc, {
      pilotId,
      data : [{ documentId, verificationStatus : next }],
    })
    const outcomeNext = readBatchOutcome(result, 1, 'updated')

    /* ⚠️ Refetch, do not patch from the response.
       `updateAll` does NOT return `allowedTransitions` — only `getInsights` and
       `tagRecommendations` compute it. And a status change is precisely the
       operation that changes which transitions are legal next, so the values
       already in the store are now stale too.
       Patching from the response left the dropdown showing only the current
       status; keeping the old array would have offered transitions the server
       has since withdrawn. Only a refetch gives the real answer. */
    if (outcomeNext.kind !== 'FAILURE') {
      const updated = outcomeNext.insights.find((i) => i.documentId === documentId)
      // Apply the returned fields first so the new status paints immediately,
      // then refetch for the recomputed transitions.
      if (updated) dispatch(upsertInsight(updated))
      await refetch()
    }

    setIsRunning(false)
    setOutcome(outcomeNext)

    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.QA_STATUS_CHANGED, { to : next })

    return outcomeNext
  }, [rc, pilotId, dispatch, refetch])

  return {
    isRunning,
    outcome,
    clearOutcome : () => setOutcome(undefined),
    publish,
    unpublish,
    discardDrafts,
    changeQaStatus,
  }
}
